/**
 * Create-prepared payload when duplicating or copying sent → draft (work date + shifted schedule).
 * For Media Devoted | Created By Ciro Autiero
 */

import { toClickIdStructure, toDomainScheme } from '@trafficpulse/shared';

import { filterSenderNamesMax11 } from '@/features/smswiser-campaigns/components/PrepareNewDropsForm';
import type { PreparedDrop } from '@/features/smswiser-campaigns/types/workspace.types';

import { shiftedExecuteAfterForCopiedDrop } from './shiftedExecuteAfterForCopiedDrop';

export type CopiedPreparedSenderMode = 'duplicate' | 'copy-as-draft';

/**
 * AI settings only survive the copy if ALL of them made it — the create endpoint
 * requires every one once AI is on, so carrying the flag with a gap in the
 * settings produced a 400 for the whole batch.
 *
 * Older rows hold exactly that gap: an edit used to be able to switch AI on
 * without the settings, so the source row has `enabled: true` and nulls. A copy
 * of one of those now simply comes across with AI off rather than blocking the
 * 60 healthy rows it was selected alongside.
 */
function copiedAiSettings(d: PreparedDrop): Record<string, unknown> {
  const complete =
    d.aiOptimizationEnabled === true &&
    d.aiOptimizationType != null &&
    d.aiOptimizationBatchAmount != null &&
    d.aiOptimizationMinimalValuePercent != null &&
    d.aiOptimizationSendUntil != null;
  if (!complete) return { aiOptimizationEnabled: false };
  return {
    aiOptimizationEnabled: true,
    aiOptimizationType: d.aiOptimizationType,
    aiOptimizationBatchAmount: d.aiOptimizationBatchAmount,
    aiOptimizationMinimalValuePercent: d.aiOptimizationMinimalValuePercent,
    aiOptimizationSendUntil: d.aiOptimizationSendUntil,
  };
}

export function buildCopiedPreparedDropPayload(
  d: PreparedDrop,
  targetWorkDate: string,
  senderMode: CopiedPreparedSenderMode,
): Record<string, unknown> {
  const senderNames =
    senderMode === 'copy-as-draft'
      ? filterSenderNamesMax11(d.senderNames ?? [])
      : (d.senderNames ?? []);
  // The source's own work day comes along, so a drop planned for the day AFTER
  // the one it belongs to keeps that gap on the copy instead of being pulled
  // back onto the target day.
  const executeAfter = shiftedExecuteAfterForCopiedDrop(
    d.executeAfter,
    targetWorkDate,
    d.workDate,
  );
  return {
    workDate: targetWorkDate,
    routeId: d.routeId,
    leadsListWiserId: d.leadsListWiserId,
    shortLinkTldValue: d.shortLinkTldValue,
    amount: d.amount,
    batchAmount: d.batchAmount ?? undefined,
    batchDelay: d.batchDelay ?? undefined,
    forceSend: d.forceSend ?? false,
    ...copiedAiSettings(d),
    useOnlyNewDomains: d.useOnlyNewDomains ?? false,
    clickIdStructure: toClickIdStructure(d.clickIdStructure),
    domainScheme: toDomainScheme(d.domainScheme),
    executeAfter,
    staticTagId: d.staticTagId ?? undefined,
    senderNames,
    smsTexts: d.smsTexts ?? [],
    rotators: d.rotators ?? undefined,
  };
}
